/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Box,
  Typography,
  TablePagination,
  Stack,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import TextField from "src/components/core/TextField";
import Button from "src/components/core/Button";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import { styled as muiStyled } from "@mui/material/styles";
import ImageIcon from "@mui/icons-material/Image";
import SearchIcon from "@mui/icons-material/Search";
import { IOSSwitch } from "src/components/core/Switch";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import MoodAddDialog from "./MoodAddDialog";
import Dropdown from "src/components/core/Dropdown";

import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";

import {
  getAllMoodsByAdmin,
  togglePublicMood,
  deleteMood,
  getRoomDetail,
} from "src/api/shopThisMood";

import moment from "moment";
import { useAuth } from "src/hooks/useAuth";
import CollapsibleTable from "src/components/core/CollapsibleTable";

const BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  // display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

const ImageNoData = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/no+data.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const TextNoData = styled.pre`
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: #f2f1f3;
  border-radius: 8px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const ImgDisplay = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
  flex-shrink: 0;
`;

const drawerWidth = 260;

const Main = muiStyled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    position: "relative",
  }),
}));

const DrawerHeader = muiStyled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  height: "75px",
}));

function NoRowsOverlay() {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <ImageNoData></ImageNoData>
      <TextNoData>No Data</TextNoData>
    </Stack>
  );
}

const RoomMoodList = ({ open }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const { roomId } = useParams();

  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  const [room, setRoom] = useState(null);
  const [moods, setMoods] = useState({
    data: [],
    total: 0,
  });

  const [dataSearch, setDataSearch] = useState({
    keyword: null,
    page: 0,
    page_size: 10,
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  const columns = [
    {
      field: "mood_name",
      label: "Name",
      width: "35%",
      sortable: false,
      align: "left",
      userRoles: [1],
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {!!params?.row?.media?.image === false ? (
              <ImageEmpty>
                <InsertPhotoIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.54)",
                    cursor: "pointer",
                  }}
                />
              </ImageEmpty>
            ) : (
              <ImgDisplay
                src={
                  params?.row?.media?.image
                    ? params?.row?.media?.image.replace(".com", ".com/300x300")
                    : ""
                }
                alt={params?.row?.media?.image}
              />
            )}
            <Stack style={{ marginLeft: 16 }}>
              <Typography component="span">{params?.row?.mood_name}</Typography>
              <Typography component="span">
                {params?.row?.room?.room_th}
              </Typography>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "createdAt",
      label: "Created Date",
      width: "16.25%",
      sortable: true,
      align: "left",
      userRoles: [1],
      renderCell: (params) =>
        moment(params?.row?.createdAt).format("DD-MM-YYYY"),
    },
    {
      field: "updatedAt",
      label: "Updated Date",
      width: "16.25%",
      sortable: true,
      align: "left",
      userRoles: [1],
      renderCell: (params) =>
        moment(params?.row?.updatedAt).format("DD-MM-YYYY"),
    },
    {
      field: "is_public",
      label: "Public",
      width: "16.25%",
      sortable: false,
      align: "left",
      userRoles: [1],
      renderCell: (params) => (
        <FormControlLabel
          sx={{
            marginInline: 0,
          }}
          control={
            <IOSSwitch
              checked={params?.row?.is_public}
              onChange={(e) => {
                if (!params?.row?.room?.is_public) {
                  enqueueSnackbar(
                    `ไม่สามารถปรับการมองเห็นภาพนี้ได้ เนื่องจาก
                  ”${params?.row?.room?.room_th}” ถูกซ่อนอยู่
                  กรุณาเปิดแสดงการมองเห็น ”${params?.row?.room?.room_th}” ก่อน`,
                    {
                      variant: "error",
                      autoHideDuration: 5000,
                    },
                  );
                } else {
                  handleTogglePublic(params?.row?._id, e.target.checked);
                }
              }}
            />
          }
        />
      ),
    },
    {
      field: "actions",
      label: "Actions",
      width: "16.25%",
      sortable: false,
      align: "left",
      userRoles: [1],
      renderCell: (params) => (
        <Dropdown
          {...params}
          options={[
            {
              label: "แก้ไข",
              startIcon: <EditRoundedIcon />,
              onClick: () => {
                openDialog({
                  type: "custom",
                  customDialog: (
                    <MoodAddDialog
                      initialValue={params?.row}
                      disableRoomField // in case of open dialog from room mood list page
                      onClose={() => closeDialog()}
                      onSuccess={() => {
                        fetchMoods();
                        closeAllDialogs();
                      }}
                      editable
                    />
                  ),
                });
              },
            },
            {
              label: "ลบ",
              startIcon: <DeleteOutlineRoundedIcon />,
              onClick: () => {
                openDialog({
                  type: "confirm",
                  title: "คุณต้องการลบใช่หรือไม่?",
                  onConfirm: () => {
                    handleDeleteMood(params?.row?._id);
                    closeDialog();
                  },
                  maxWidth: 342,
                });
              },
            },
          ]}
        />
      ),
    },
  ];

  const visibleColumns = columns.filter((col) =>
    col.userRoles.includes(user?.role_id),
  );

  const handleTogglePublic = async (id, isPublic) => {
    await togglePublicMood(id, isPublic);
    await fetchMoods();

    // let updated = moods.data.map((mood) => {
    //   if (mood._id === id) {
    //     return {
    //       ...mood,
    //       is_public: isPublic,
    //     };
    //   } else {
    //     return mood;
    //   }
    // });

    // setMoods({
    //   ...moods,
    //   data: updated,
    // });
  };

  const handlePageChange = (newPage) => {
    setDataSearch({ ...dataSearch, page: newPage });
  };

  const handleSort = (sortBy) => {
    setDataSearch({
      ...dataSearch,
      sortBy: sortBy,
      sortOrder:
        dataSearch.sortBy === sortBy && dataSearch.sortOrder === "asc"
          ? "desc"
          : "asc",
    });
  };

  const onSearchButton = () => {
    setDataSearch({ ...dataSearch, page: 0 });
    (async () => {
      fetchMoods();
    })();
  };

  const fetchMoods = async () => {
    try {
      const { result, total } = await getAllMoodsByAdmin({
        room: roomId,
        ...dataSearch,
      });
      setMoods({
        data: result,
        total: total,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRoomDetail = async () => {
    try {
      const data = await getRoomDetail(roomId);
      setRoom(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteMood = async (id) => {
    try {
      await deleteMood(id);
      await fetchMoods();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!roomId) return;
    fetchMoods();
    fetchRoomDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  useEffect(() => {
    fetchMoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch.page, dataSearch.sortBy, dataSearch.sortOrder]);

  return (
    <>
      <Main open={open}>
        <DrawerHeader />
        <BoxWrapper>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                mr: 2,
              }}
            >
              <IconButton
                onClick={() => window.history.back()}
                sx={{
                  mr: 1,
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  mr: 2,
                }}
              >
                {room?.room_th}
              </Typography>
              <TextField
                placeholder="ค้นหาโดย ชื่อมูด / ชื่อสไตล์"
                sx={{
                  width: "100%",
                  // flexShrink: 0,
                }}
                InputProps={{
                  endAdornment: (
                    <SearchIcon
                      sx={{
                        color: "#e04132",
                        cursor: "pointer",
                      }}
                    />
                  ),
                }}
                onChange={(e) => {
                  setDataSearch({
                    ...dataSearch,
                    keyword: e.target.value,
                  });
                }}
                onKeyUp={(e) => (e.code === "Enter" ? onSearchButton() : null)}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<ImageIcon />}
              onClick={() =>
                openDialog({
                  type: "custom",
                  customDialog: (
                    <MoodAddDialog
                      initialValue={{
                        room,
                      }}
                      disableRoomField // in case of open dialog from room mood list page
                      onClose={() => closeDialog()}
                      onSuccess={() => {
                        fetchMoods();
                        closeAllDialogs();
                      }}
                    />
                  ),
                })
              }
              size="large"
              sx={{
                minWidth: 200,
              }}
            >
              เพิ่มภาพมูด
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <Typography
              sx={{
                mr: 2,
              }}
              component="span"
            >
              ภาพมูดทั้งหมด :{" "}
              <Typography
                component="span"
                sx={{
                  color: "#E13B30",
                }}
              >
                {moods.total}
              </Typography>
            </Typography>
          </Box>
          <CollapsibleTable
            rows={moods.data}
            collapsible={false}
            columns={visibleColumns}
            components={{
              NoRowsOverlay,
              // NoResultsOverlay,
            }}
            sortBy={dataSearch.sortBy}
            sortOrder={dataSearch.sortOrder}
            onSort={handleSort}
          />
          <TablePagination
            component="div"
            count={moods.total}
            rowsPerPageOptions={[10]} // provide only 1 option to hide rows per page label
            rowsPerPage={10}
            page={dataSearch.page}
            onPageChange={(e, newPage) => handlePageChange(newPage)}
          />
        </BoxWrapper>
      </Main>
    </>
  );
};

export default RoomMoodList;
